import {
  QualityEventAnswers,
  QualityEventField,
  QualityEventRecord,
  QualityEventStep,
} from "src/types/quality-events";
import { linkedQualityEventRows } from "./quality-event-linked-rows";

export const shouldShowQualityEventOutstandingFields = (
  record: QualityEventRecord,
  stepKey: string
) => {
  const savedStep = record.steps[stepKey];
  if (!savedStep?.updatedAt) return false;

  const assignment = record.definition.definition.assignment;
  const automaticallyAssignedField =
    assignment?.stepKey === stepKey ? assignment.assigneeField : null;
  const isBlankNewRecord =
    record.version === 1 &&
    record.definition.definition.steps[0]?.key === stepKey &&
    !record.title.trim() &&
    Object.entries(savedStep.answers).every(
      ([key, value]) =>
        key === automaticallyAssignedField && value === record.createdBy.id
    );

  return !isBlankNewRecord;
};

export const isQualityEventConditionMet = (
  condition: QualityEventField["showWhen"],
  answers: QualityEventAnswers
) => {
  if (!condition) return true;
  const value = answers[condition.fieldKey];
  if ("oneOf" in condition)
    return condition.oneOf.some((option) => value === option);
  return "contains" in condition
    ? Array.isArray(value) && value.some((item) => item === condition.contains)
    : value === condition.equals;
};

export const isQualityEventFieldVisible = (
  field: QualityEventField,
  answers: QualityEventAnswers
) => isQualityEventConditionMet(field.showWhen, answers);

export const isQualityEventFieldRequired = (
  field: QualityEventField,
  answers: QualityEventAnswers
) =>
  !!(
    field.required ||
    (field.requiredWhen &&
      isQualityEventConditionMet(field.requiredWhen, answers))
  );

const isAnswerEmpty = (value: unknown) =>
  value == null ||
  (typeof value === "string" &&
    !value
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim()) ||
  (Array.isArray(value) && !value.length);

export const getQualityEventFieldRequirement = (
  field: QualityEventField,
  value: unknown,
  answers: QualityEventAnswers,
  requirements: QualityEventStep["completionRequirements"] = []
): string | undefined => {
  if (
    isQualityEventFieldRequired(field, answers) &&
    (isAnswerEmpty(value) || (field.type === "checkbox" && value !== true))
  )
    return "Required to complete this step.";

  return requirements.find((requirement) =>
    "atLeastOneFields" in requirement
      ? requirement.atLeastOneFields.includes(field.key) &&
        requirement.atLeastOneFields.every((key) => isAnswerEmpty(answers[key]))
      : requirement.fieldKey === field.key &&
        !isQualityEventConditionMet(requirement, answers)
  )?.message;
};

export const getIncompleteQualityEventFieldPaths = ({
  fields,
  values,
  allAnswers,
  sourceAnswersByStep,
  completionRequirements,
  files,
  requiredFieldPaths,
  validateAllRequirements,
}: {
  fields: QualityEventField[];
  values: QualityEventAnswers;
  allAnswers: QualityEventAnswers;
  sourceAnswersByStep: Record<string, QualityEventAnswers>;
  completionRequirements?: QualityEventStep["completionRequirements"];
  files: Record<string, File[]>;
  requiredFieldPaths: Set<string>;
  validateAllRequirements?: boolean;
}) => {
  const incomplete = new Set<string>();
  const visit = (
    currentFields: QualityEventField[],
    currentValues: QualityEventAnswers,
    fieldPathPrefix = "",
    requirements = completionRequirements
  ) => {
    const scope = { ...allAnswers, ...currentValues };
    currentFields
      .filter((field) => isQualityEventFieldVisible(field, scope))
      .forEach((field) => {
        if (field.type === "approval") return;
        const fieldPath = `${fieldPathPrefix}${field.key}`;
        const rawValue =
          field.type === "file" && files[fieldPath]?.length
            ? files[fieldPath]
            : currentValues[field.key];
        const value =
          field.type === "repeatable" && field.sourceRows
            ? linkedQualityEventRows(
                field,
                rawValue,
                sourceAnswersByStep[field.sourceRows.stepKey] ?? {}
              )
            : rawValue;
        if (
          (validateAllRequirements || requiredFieldPaths.has(fieldPath)) &&
          getQualityEventFieldRequirement(field, value, scope, requirements)
        ) {
          incomplete.add(fieldPath);
        }
        if (field.type !== "repeatable" || !Array.isArray(value)) return;
        value.forEach((row, index) => {
          if (!row || typeof row !== "object") return;
          const id = "id" in row && typeof row.id === "string" ? row.id : index;
          const answers =
            "answers" in row &&
            row.answers &&
            typeof row.answers === "object" &&
            !Array.isArray(row.answers)
              ? (row.answers as QualityEventAnswers)
              : {};
          visit(
            field.fields,
            answers,
            `${fieldPath}:${id}:`,
            field.completionRequirements ?? []
          );
        });
      });
  };

  visit(fields, values);
  return incomplete;
};
